<template>
    <div class="create">
        <portal to="title">Create patient</portal>

        <vue-form-card class="mt-10">
            <template v-slot:title>Patient details</template>

            <template v-slot:description>
                Please be assured that this information will be private and stored securely
            </template>

            <template v-slot:body>
                <div class="row mt-5">
                    <div class="col-12 col-md-6">
                        <vue-text-input
                            v-model="form.firstName"
                            label="First name"
                            :required="true"
                            :errors="formErrors['firstName']"
                        />
                    </div>

                    <div class="col-12 col-md-6">
                        <vue-text-input
                            v-model="form.lastName"
                            label="Last name"
                            :required="true"
                            :errors="formErrors['lastName']"
                        />
                    </div>
                </div>

                <div class="row mt-5">
                    <div class="col-12 col-md-6">
                        <vue-check-group-input
                            v-model="form.biologicalSex"
                            label="Biological sex"
                            :radio="true"
                            :data="genders"
                        />
                    </div>

                    <div class="col-12 col-md-6">
                        <vue-check-group-input
                            v-model="form.identifyAs"
                            label="Identify as"
                            :radio="true"
                            :data="genders"
                        />
                    </div>
                </div>

                <div class="row mt-5">
                    <div class="col-12 col-md-6">
                        <vue-text-input
                            v-model="form.phoneNumber"
                            label="Phone number"
                            :errors="formErrors['phoneNumber']"
                        />
                    </div>

                    <div class="col-12 col-md-6">
                        <vue-text-input
                            v-model="form.knownAs"
                            label="Known as"
                            :errors="formErrors['knownAs']"
                            :required="false"
                        />
                    </div>
                </div>

                <vue-text-input
                    class="mt-5"
                    label="Date of birth"
                    placeholder="mm/dd/yyyy"
                    type="date"
                    :required="true"
                    :errors="formErrors['dateOfBirth']"
                    v-model="form.dateOfBirth"
                />

                <vue-textarea-input
                    class="mt-5"
                    label="Please set out relevant information about this patient's condition"
                    v-model="form.additionalInfo"
                    :required="true"
                    :errors="formErrors['additionalInfo']"
                />
            </template>
        </vue-form-card>

        <vue-form-card class="mt-10">
            <template v-slot:title>General Practioner</template>

            <template v-slot:description>
                Please be assured that this information will be private and stored securely
            </template>

            <template v-slot:body>
                <div class="row mt-5">
                    <div class="col-12 col-md-6">
                        <vue-text-input
                            v-model="form.practionerName"
                            label="Name"
                            :errors="formErrors['practionerName']"
                        />
                    </div>

                    <div class="col-12 col-md-6">
                        <vue-text-input
                            v-model="form.practionerPractice"
                            label="Practice"
                            :errors="formErrors['practionerPractice']"
                        />
                    </div>
                </div>

                <div class="row mt-5">
                    <div class="col-12 col-md-6">
                        <vue-text-input
                            v-model="form.practionerEmail"
                            label="Email"
                            :errors="formErrors['practionerEmail']"
                        />
                    </div>

                    <div class="col-12 col-md-6">
                        <vue-text-input
                            v-model="form.practionerPhoneNumber"
                            label="Phone number"
                            :errors="formErrors['practionerPhoneNumber']"
                        />
                    </div>
                </div>
            </template>
        </vue-form-card>

        <vue-form-card class="mt-10">
            <template v-slot:title>Address</template>

            <template v-slot:description>
                Please be assured that this information will be private and stored securely
            </template>

            <template v-slot:body>
                <vue-text-input
                    label="Postal code"
                    class="my-5"
                    v-model="form.address.postalCode"
                    :errors="formErrors['address.postalCode']"
                    :loading="loading.postalCode"
                />

                <vue-text-input
                    label="Street address"
                    class="mb-5"
                    v-model="form.address.line1"
                    placeholder="Street and number, P.O.box, c/o"
                    :errors="formErrors['address.line1']"
                />

                <vue-text-input
                    label="Street address 2"
                    class="mb-5"
                    v-model="form.address.line2"
                    placeholder="Flat, suite, unit, building, floor, etc"
                    :errors="formErrors['address.line2']"
                />

                <div class="row">
                    <div class="col-12 col-md-6">
                        <vue-text-input
                            class="mb-5 mb-md-0"
                            label="City"
                            v-model="form.address.city"
                            :errors="formErrors['address.city']"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <vue-select-input
                            label="County"
                            :loading="loading.counties"
                            :data="counties"
                            :data-value="(item) => item.id"
                            :data-text="(item) => item.name"
                            v-on:page-changed="countiesPaginator.page = $event"
                            v-on:search="searchCounties"
                            v-model="form.address.county"
                            :errors="formErrors['address.county']"
                        />
                    </div>
                </div>
            </template>
        </vue-form-card>

        <div class="mb-5 text-center py-8">
            <button class="btn btn-outline-primary mr-4" type="button" v-on:click="$router.back()">Cancel</button>
            <button type="button" class="btn btn-primary w-50" v-on:click="create" :disabled="loading.create">
                <i class="fa fa-spinner fa-spin" v-if="loading.create"></i>
                Save
            </button>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';

    import VueFormCard from '@components/formCard';
    import VueTextInput from '@components/textInput';
    import VueTextareaInput from '@components/textareaInput';
    import VueSelectInput from '@components/selectInput';
    import VueCheckGroupInput from '@components/checkGroupInput';

    import patientsService from '@api/patients';
    import addressesService from '@api/addresses';

    import { debounce } from 'lodash';

    export default Vue.extend({
        components: {
            VueFormCard,
            VueTextInput,
            VueTextareaInput,
            VueSelectInput,
            VueCheckGroupInput
        },
        data() {
            return {
                form: {
                    firstName: '',
                    lastName: '',
                    biologicalSex: 'male',
                    identifyAs: 'male',
                    phoneNumber: '',
                    knownAs: '',
                    dateOfBirth: '',
                    additionalInfo: '',
                    practionerName: '',
                    practionerPractice: '',
                    practionerEmail: '',
                    practionerPhoneNumber: '',
                    address: addressesService.defaultInput()
                },
                formErrors: {},
                counties: {},
                countiesPaginator: countiesPaginator(),
                genders: genders(),
                loading: {
                    create: false,
                    counties: false,
                    postalCode: false
                }
            };
        },
        watch: {
            countiesPaginator: {
                deep: true,
                handler() {
                    this.fetchCounties();
                }
            },
            'form.address.postalCode'() {
                this.checkPostalCode();
            }
        },
        methods: {
            fetchCounties() {
                this.loading.counties = true;

                addressesService
                    .counties(this.countiesPaginator)
                    .then((response) => {
                        this.counties = response.data;
                    })
                    .catch((error) => {
                        window.location.href = `/error/${error.response.status}`;
                    })
                    .finally(() => {
                        this.loading.counties = false;
                    });
            },
            create() {
                this.formErrors = {};
                this.loading.create = true;

                patientsService
                    .create(this.form)
                    .then((response) => {
                        this.$router.push('/patients');
                    })
                    .catch((error) => {
                        if (error.response.data.errors) {
                            this.formErrors = error.response.data.errors;
                            this.$toasted.error(
                                'Please enter a valid value in all the required fields before proceeding.',
                                { icon: 'exclamation-triangle' }
                            );
                            this.$goToError();
                        } else {
                            window.location.href = `/error/${error.response.status}`;
                        }
                    })
                    .finally(() => {
                        this.loading.create = false;
                    });
            },
            searchCounties: debounce(function(term) {
                this.countiesPaginator.page = 1;
                this.countiesPaginator.term = term;
            }, 500),
            checkPostalCode: debounce(function() {
                if (this.formErrors && this.formErrors['address.postalCode']) {
                    this.$delete(this.formErrors, 'address.postalCode');
                }

                this.form.address.latitude = null;
                this.form.address.longitude = null;
                this.loading.postalCode = true;

                addressesService
                    .checkPostalCode(this.form.address.postalCode)
                    .then((response) => {
                        this.form.address.latitude = response.data.result.latitude;
                        this.form.address.longitude = response.data.result.longitude;
                    })
                    .catch((error) => {
                        this.$set(this.formErrors, 'address.postalCode', ['The provided postal code is invalid']);
                    })
                    .finally(() => {
                        this.loading.postalCode = false;
                    });
            }, 500)
        },
        mounted() {
            this.fetchCounties();
        }
    });

    /** Options */
    const genders = () => [
        { id: 'male', text: 'Male' },
        { id: 'female', text: 'Female' }
    ];

    /** Paginators */
    const countiesPaginator = () => {
        return {
            term: null,
            page: 1
        };
    };
</script>
